import * as Yup from 'yup';
import { DateTime } from 'luxon';

import PaymentForm from './Components/PaymentForm';

import BalanceInfo from './Components/BalanceInfo';

import OpenBills from './Components/OpenBills';

import { validateLuhn, validateExpiryDate } from '../../utils/validation';

import { methods } from '../Payments/helpers';

let methodObj = methods.reduce((acc, method) => ({...acc, [method.value]: method.label}), {});

const headers = [
  {
    label: "Payment Num",
    id: "vendor_payment_num"
  },
  {
    label: 'Vendor',
    id: 'vendor_name'
  },
  {
    label: 'Payment Method',
    id: 'method',
    render: row => methodObj[row.method]
  },
  {
    label: 'Amount',
    id: 'display_amount',
    currency: true
  },
  {
    label: 'Status',
    id: 'status',
    multi: true,
    values: [{value: "accepted", status: "success", label: "Accepted"}, {value: "declined", status: "error", label: "Declined"}]
  },
  {
    id: "available_amount",
    label: "Available Amount",
    currency: true
  },
  {
    label: "Date",
    id: "date",
    date: true
  }
];

const filters = [
  {
    id: "available_amount",
    type: "balance",
    label: "Available Amount",
  },
  {
    id: "amount",
    type: "number",
    label: "Amount",
  },
  {
    id: "date",
    type: "date",
    label: "Date",
  }
]

const fields = [
 { 
    fieldType: "debounceSearchSolo",
    formFieldType: "select",
    select: [],
    name: "vendor_id",
    label: "Select Vendor",
    url: "vendors",
    fieldName: "vendor_id",
    displayField: "full_name",
    formUpdates: [{name: "vendor_name", field: "full_name"}],
    setDisplayField: "vendor_name",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "formField",
    formFieldType: "date",
    name: "date",
    label: "Payment Date",
    type: "date",
    size: {xs: 12, lg: 3},
  },
  // {
  //   fieldType: "divider",
  //   header: "Vendor Details",
  //   name: "vendor_details",
  //   size: {xs: 12, lg: 12},
  // },
  {
    fieldType: "Component",
    Component: <BalanceInfo />,
    name: "balance_info",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "divider",
    header: "Payment Details",
    name: "payment_details",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "Component",
    Component: <PaymentForm />,
    name: "payment",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "divider",
    header: "Open Bills",
    name: "bills_details",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "Component",
    Component: <OpenBills />,
    name: "bills",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "divider",
    header: "Notes",
    name: "notes",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "notes",
    label: "Notes",
    type: "text",
    multiline: true,
    rows: 4,
    size: {xs: 12, lg: 12},
  },
]


const initialValues = {
  vendor_id: '',
  vendor_name: '',
  date: DateTime.local(),
  method: '',
  info: '',
  amount: "",
  final_balance: 0,
  notes: '',
  card_num: '',
  exp: '',
  cvv: '',
  entry: 'manual',
  processed: false
}

const additionalState = {
  vendor_name: '',
  vendor_phone: '',
  applied_payments: [],
}

const schema = Yup.object().shape({
  vendor_id: Yup.string().required('Vendor is required'),
  amount: Yup.number().min(0, 'Amount must be greater than 0').required('Amount is required'),
  method: Yup.string().required('Payment Method is required'),
  entry: Yup.string().required('Payment Entry is required'),
  
  // info: Yup.string().when('entry', {
  //   is: 'manual',
  //   then: (schema) => schema.required('Payment Info is required'),
  //   otherwise: (schema) => schema
  // }),

  card_num: Yup.string().when('entry', {
    is: (val) => val !== 'manual',
    then: (schema) => schema
      .required('Credit Card Number is required')
      .test('len', 'Invalid card length', (val) => {
        // Remove all non-digits before checking length
        const rawDigits = val?.replace(/\D/g, ''); 
        return rawDigits?.length === 15 || rawDigits?.length === 16;
      })
      .test('luhn-check', 'Invalid card number (Check numbers)', (val) => {
        const rawDigits = val?.replace(/\D/g, '');
        return validateLuhn(rawDigits);
      }),
    otherwise: (schema) => schema
  }),

  exp: Yup.string().when('entry', {
    is: (val) => val !== 'manual',
    then: (schema) => schema
      .required('Expiration Date is required')
      .test('valid-expiry', 'Card is expired or month is invalid', (val) => validateExpiryDate(val)),
    otherwise: (schema) => schema
  }),

  cvv: Yup.string().when('entry', {
    is: (val) => val !== 'manual',
    then: (schema) => schema
      .required('CVV is required')
      .test('len', 'Must be 3 or 4 digits', (val) => val?.length === 3 || val?.length === 4),
    otherwise: (schema) => schema
  }),

  processed: Yup.boolean().when('entry', {
    is: (val) => val !== 'manual',
    then: (schema) => schema.oneOf([true], 'Payment must be processed first'),
    otherwise: (schema) => schema
  })
});

const parseData = (data) => {
  const parsed = {...data};
  parsed.date = DateTime.fromISO(data.date);
  if (parsed.vendor_id) parsed.vendor_id = data.vendor_id.toString();
  parsed.entry = "manual";
  return parsed
}

export { headers, filters, fields, schema, initialValues, parseData, additionalState };